import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// ── Types ────────────────────────────────────────────────────────
export type AttentionSeverity = 'critical' | 'warning' | 'none';

export interface AdminProducerRow {
  id: string;
  name: string;
  city: string | null;
  region: string | null;
  categories: string[];
  status: string;        // 'draft' | 'public'
  verified: boolean;
  updated_at: string;
  // Maturity
  maturity_score: number;
  maturity_level: number;
  has_cover_image: boolean;
  has_any_image: boolean;
  has_activity: boolean;
  has_place: boolean;
  // Attention
  needs_attention: boolean;
  attention_severity: AttentionSeverity;
  attention_reason: string | null;
}

export type ProducerSegment = 'all' | 'attention' | 'public' | 'draft';

export function useAdminProducersWithMaturity() {

  return useQuery({
    queryKey: ['admin-producers-maturity'],
    queryFn: async (): Promise<AdminProducerRow[]> => {
      // Fetch producers
      const { data: producers, error: pErr } = await supabase
        .from('producers')
        .select('id, name, city, region, categories, status, verified, updated_at')
        .order('updated_at', { ascending: false });

      if (pErr) throw pErr;
      if (!producers || producers.length === 0) return [];

      const ids = producers.map((p) => p.id);

      // Fetch maturity + attention views in parallel
      const [matRes, attRes] = await Promise.all([
        supabase
          .from('producer_maturity_v1' as any)
          .select('producer_id, maturity_score, has_cover_image, has_any_image, has_activity, has_place')
          .in('producer_id', ids),
        supabase.from('producer_attention_v1' as any).select('*').in('producer_id', ids),
      ]);

      if (matRes.error) throw matRes.error;
      if (attRes.error) throw attRes.error;

      const matMap = new Map((matRes.data as any[]).map((m) => [m.producer_id, m]));
      const attMap = new Map((attRes.data as any[]).map((a) => [a.producer_id, a]));

      // Merge
      let rows: AdminProducerRow[] = producers.map((p) => {
        const mat = matMap.get(p.id) || {};
        const att = attMap.get(p.id) || {};
        return {
          id: p.id,
          name: p.name,
          city: p.city,
          region: p.region,
          categories: p.categories || [],
          status: p.status,
          verified: p.verified,
          updated_at: p.updated_at,
          maturity_score: mat.maturity_score ?? 0,
          maturity_level: mat.maturity_level ?? 0,
          has_cover_image: mat.has_cover_image ?? false,
          has_any_image: mat.has_any_image ?? false,
          has_activity: mat.has_activity ?? false,
          has_place: mat.has_place ?? false,
          needs_attention: att.needs_attention ?? false,
          attention_severity: (att.attention_severity as AttentionSeverity) ?? 'none',
          attention_reason: att.attention_reason ?? null,
        };
      });

      // Sort: attention first, then low maturity, then recent
      rows.sort((a, b) => {
        // needs_attention desc
        if (a.needs_attention !== b.needs_attention) return a.needs_attention ? -1 : 1;
        // severity: critical > warning > none
        const sevOrder = { critical: 0, warning: 1, none: 2 };
        const sevDiff = sevOrder[a.attention_severity] - sevOrder[b.attention_severity];
        if (sevDiff !== 0) return sevDiff;
        // maturity_score asc
        if (a.maturity_score !== b.maturity_score) return a.maturity_score - b.maturity_score;
        // updated_at desc
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      });

      return rows;
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 120_000,
  });
}

// Dashboard summary hook
export function useProducerAttentionSummary() {
  return useQuery({
    queryKey: ['admin-producer-attention-summary'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('producer_attention_v1' as any)
        .select('needs_attention, attention_severity, attention_reason');

      if (error) throw error;

      const rows = (data as any[]) || [];
      const needsAttention = rows.filter((r) => r.needs_attention).length;
      const readyToPublish = rows.filter((r) => r.attention_reason === 'Redo att publiceras').length;
      const publicNotVerified = rows.filter((r) => r.attention_reason === 'Public men ej verifierad').length;

      return { needsAttention, readyToPublish, publicNotVerified };
    },
    staleTime: 60_000,
  });
}
